import { useMemo, useState } from 'react';
import type { CustomField, FeederFieldConfig, RuleSet } from '@kkp-fraud/shared';
import { useShell } from '../components/AppLayout';
import { CustomFieldDialog } from '../components/CustomFieldDialog';
import { PageHead, Tip } from '../components/PageHead';
import { FIELD_KINDS, FIELD_NAMES, fieldHint, fieldLabel, type FieldName } from '../fields';
import { t } from '../i18n';
import { FEEDER_STATUSES, effectiveFeederConfig, type FeederConfig } from '../feederFields';
import { useFeederConfig } from '../hooks/useFeederConfig';
import { fieldsUsedBy } from '../sentence';
import { isViewerOnly } from '../session';
// R8b per-rule-set permissions
import { mayOnSet } from '../session';
import { FIELD_DEPENDS, FIELD_ORIGINS, SOURCE_IDS, fieldsOnlyFrom, type SourceId } from '../sources';

/**
 * Field Management — one table: the feeder payload (docs/specs/wave6/field-bindings.md).
 *
 * The feeder payload is configuration: it is edited into the draft as `RuleSet.feederFields`
 * and released through the same propose/approve path as a rule. Each row says what it is used
 * as — the standard fields the engine computes from it (read-only), the custom field it is bound
 * to (`RuleSet.customFields`, which the engine reads by name), or that it is not bound and no
 * rule can use it yet — and what those fields mean. A binding is made, edited or undone on its
 * row. Under the table, only when there are any: the standard fields no row feeds (the engine
 * computes them), and a custom field whose key is no longer in the payload.
 */

function kindChip(kind: string) {
  const cls = kind === 'number' ? '' : kind === 'boolean' ? 'warn' : '';
  return <span className={`chip ${cls}`}>{t(`fieldkind.${kind}.label`)}</span>;
}

const FEEDER_KEY = /^[a-z][a-z0-9_]*$/;

type RowProblem = 'name' | 'key_invalid' | 'key_duplicate';

/** What stops a row from being saved — shown next to the row, and Save stays disabled. */
function rowProblems(rows: readonly FeederFieldConfig[]): Map<number, RowProblem[]> {
  const seen = new Map<string, number>();
  for (const r of rows) seen.set(r.key, (seen.get(r.key) ?? 0) + 1);
  const out = new Map<number, RowProblem[]>();
  for (const r of rows) {
    const list: RowProblem[] = [];
    if (r.name.trim() === '') list.push('name');
    if (!FEEDER_KEY.test(r.key)) list.push('key_invalid');
    else if ((seen.get(r.key) ?? 0) > 1) list.push('key_duplicate');
    if (list.length > 0) out.set(r.no, list);
  }
  return out;
}

/** Every field some rule in the document reads — standard or custom — with the rules that read it. */
function fieldReaders(rs: RuleSet | null): Map<string, string[]> {
  const out = new Map<string, string[]>();
  const add = (f: string, id: string) => out.set(f, [...(out.get(f) ?? []), id]);
  for (const r of rs?.ruleBased ?? []) for (const f of fieldsUsedBy(r)) add(f, r.id);
  for (const r of rs?.patternBased ?? []) {
    const used = new Set<string>([...fieldsUsedBy(r), r.scope]);
    for (const f of used) add(f, r.id);
  }
  return out;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** The edited rows as they go into the document: DE trimmed (empty = host-supplied), name trimmed. */
function cleaned(rows: readonly FeederFieldConfig[]): FeederFieldConfig[] {
  return rows.map((r) => {
    const de = r.de?.trim() ?? '';
    return { ...r, de: de === '' ? null : de, name: r.name.trim() };
  });
}

/** The standard fields the engine computes from this raw key (`FIELD_DEPENDS`). */
function standardFieldsFrom(key: string): FieldName[] {
  return FIELD_NAMES.filter((f) => FIELD_DEPENDS[f].includes(key));
}

/**
 * The standard fields no row of `config` feeds — the engine computes them from something other
 * than one raw key (`channel_id` is the envelope's `Channel.Id`), or the payload lost their rows.
 */
function standardFieldsWithoutRow(config: FeederConfig): FieldName[] {
  const keys = new Set(config.map((r) => r.key));
  return FIELD_NAMES.filter((f) => !FIELD_DEPENDS[f].some((k) => keys.has(k)));
}

/** One line of the description column: what a standard field means, in plain words. */
function StandardMeaning({ field }: { field: FieldName }) {
  const hint = fieldHint(field);
  return (
    <span className="field-meaning">
      <span className="mono nowrap">{field}</span> — {fieldLabel(field)}
      {hint && <span className="muted"> · {hint}</span>}
    </span>
  );
}

/** One line of the description column for a custom field: its own description, else its row's name. */
function CustomMeaning({ field, rowName }: { field: CustomField; rowName?: string }) {
  const text = field.description ?? rowName;
  return (
    <span className="field-meaning">
      <span className="mono nowrap">{field.name}</span> — {text ?? <span className="muted">—</span>}
    </span>
  );
}

/** Whether the signed-in account may change the primary set's document (Fields edits it). */
function useMayEdit(): boolean {
  const { state, session, primaryAccess } = useShell();
  // R8b: the feeder mapping is part of the primary set's document — `edit` on it.
  return (primaryAccess !== undefined ? mayOnSet(session, primaryAccess, 'edit') : !isViewerOnly(session)) && state.draft !== null;
}

/**
 * Wave 6 — the draft's custom fields and the one way they are written. A list edited back to
 * exactly what is live leaves the draft byte-identical: no `customFields` key when live has none.
 */
function useCustomBindings() {
  const { state } = useShell();
  const list: readonly CustomField[] = state.draft?.customFields ?? [];
  const write = (next: CustomField[]) => {
    if (!state.draft) return;
    const live = state.loaded?.customFields;
    if (live === undefined && next.length === 0) {
      const { customFields: _gone, ...rest } = state.draft;
      state.setDraft(rest as RuleSet);
      return;
    }
    state.patchRuleSet({ customFields: live !== undefined && same(next, live) ? live : next });
  };
  const readers = useMemo(() => fieldReaders(state.draft), [state.draft]);
  return {
    list,
    /** Add, or replace the binding named `previous` (its old name when renamed). */
    save: (field: CustomField, previous?: string) => {
      const at = previous === undefined ? -1 : list.findIndex((f) => f.name === previous);
      write(at >= 0 ? list.map((f, i) => (i === at ? field : f)) : [...list, field]);
    },
    unbind: (name: string) => write(list.filter((f) => f.name !== name)),
    /** The rules that read `name` — a field in use cannot be unbound or renamed from under them. */
    readersOf: (name: string) => readers.get(name) ?? [],
  };
}

/** Which dialog is open: a binding to add from its row's key, or one to edit. */
type DialogState = { mode: 'new'; from: string } | { mode: 'edit'; field: CustomField } | null;

/** The bind / edit dialog over the draft's custom fields. */
function BindingDialog({ open, onClose }: { open: DialogState; onClose: () => void }) {
  const config = useFeederConfig();
  const bindings = useCustomBindings();
  if (!open) return null;
  const editing = open.mode === 'edit' ? open.field : undefined;
  return (
    <CustomFieldDialog
      initial={editing}
      from={open.mode === 'new' ? open.from : open.field.from}
      rows={config}
      others={bindings.list.filter((f) => f.name !== editing?.name)}
      onClose={onClose}
      onSave={(field) => {
        bindings.save(field, editing?.name);
        onClose();
      }}
    />
  );
}

/** Edit and Unbind for one custom field; Unbind is refused while a rule reads it. */
function BindingActions({ field, idPrefix, onEdit }: { field: CustomField; idPrefix: string; onEdit: () => void }) {
  const bindings = useCustomBindings();
  const rules = bindings.readersOf(field.name);
  return (
    <span className="row-wrap" style={{ flexWrap: 'nowrap' }}>
      <button
        type="button"
        className="btn small ghost"
        data-testid={`${idPrefix}-edit-${field.name}`}
        aria-label={t('fields.custom.edit.a11y', { name: field.name })}
        onClick={onEdit}
      >
        {t('fields.edit.action')}
      </button>
      <button
        type="button"
        className="btn small ghost"
        data-testid={`${idPrefix}-unbind-${field.name}`}
        aria-label={t('fields.custom.unbind.a11y', { name: field.name })}
        disabled={rules.length > 0}
        onClick={() => bindings.unbind(field.name)}
      >
        {t('fields.custom.unbind.action')}
      </button>
      {rules.length > 0 && <Tip align="right">{t('fields.custom.unbind.blocked.tip', { rules: rules.join(', '), name: field.name })}</Tip>}
    </span>
  );
}

/**
 * KKP's raw feeder fields and which source sends each — the payload in force (the draft's
 * copy, else the built-in sheet). `sends` true is a tick, false a muted dash. Edit turns
 * every row into inputs; Save writes the whole list into the draft as one setting. Outside
 * editing, every row says what it is used as.
 */
function FeederView() {
  const { state } = useShell();
  const config = useFeederConfig();
  const mayEdit = useMayEdit();
  const bindings = useCustomBindings();
  /** The rows being edited; `null` = not editing. A deep copy — the built-in rows share their `sends` objects. */
  const [rows, setRows] = useState<FeederFieldConfig[] | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const editing = rows !== null;

  const problems = useMemo(() => (rows ? rowProblems(rows) : new Map<number, RowProblem[]>()), [rows]);
  const readers = useMemo(() => fieldReaders(state.draft), [state.draft]);

  const shown: readonly FeederFieldConfig[] = rows ?? config;
  /** The custom fields each raw key is bound to. */
  const customByKey = useMemo(() => {
    const out = new Map<string, CustomField[]>();
    for (const f of bindings.list) out.set(f.from, [...(out.get(f.from) ?? []), f]);
    return out;
  }, [bindings.list]);

  const startEdit = () => setRows(JSON.parse(JSON.stringify(config)) as FeederFieldConfig[]);
  const cancel = () => setRows(null);

  const patchRow = (no: number, patch: Partial<FeederFieldConfig>) =>
    setRows((prev) => prev && prev.map((r) => (r.no === no ? { ...r, ...patch } : r)));

  const setKey = (row: FeederFieldConfig, key: string) =>
    setRows(
      (prev) =>
        prev &&
        prev.map((r) => {
          if (r.no !== row.no) return r;
          // Typing a key over an assumed one is the confirmation the flag was waiting for.
          const { keyAssumed: _confirmed, ...rest } = r;
          return { ...rest, key };
        }),
    );

  const setSends = (no: number, id: SourceId, value: boolean) =>
    setRows((prev) => prev && prev.map((r) => (r.no === no ? { ...r, sends: { ...r.sends, [id]: value } } : r)));

  const removeRow = (no: number) => setRows((prev) => prev && prev.filter((r) => r.no !== no));

  const addRow = () =>
    setRows((prev) => {
      if (!prev) return prev;
      const no = prev.reduce((max, r) => Math.max(max, r.no), 0) + 1;
      const sends = Object.fromEntries(SOURCE_IDS.map((id) => [id, false])) as FeederFieldConfig['sends'];
      return [...prev, { no, de: null, name: '', key: '', status: 'New', sends }];
    });

  /** The rules that would lose every source for some field if `key` went away. */
  const blockedBy = (key: string): { fields: FieldName[]; rules: string[] } => {
    if (!rows) return { fields: [], rules: [] };
    const fields = fieldsOnlyFrom(key, rows).filter((f) => readers.has(f));
    const rules = [...new Set(fields.flatMap((f) => readers.get(f) ?? []))];
    return { fields, rules };
  };

  const save = () => {
    if (!rows || problems.size > 0 || !state.draft) return;
    const next = cleaned(rows);
    setRows(null);
    // Nothing changed: the draft is not touched, so a clean draft stays byte-identical.
    if (same(next, config)) return;
    // Edited back to exactly what is live, and live has no copy of its own: drop the property.
    if (state.loaded && state.loaded.feederFields === undefined && same(next, effectiveFeederConfig(state.loaded))) {
      const { feederFields: _gone, ...rest } = state.draft;
      state.setDraft(rest as RuleSet);
      return;
    }
    state.patchRuleSet({ feederFields: next });
  };

  const problemText: Record<RowProblem, string> = {
    name: t('fields.feeder.problem.name'),
    key_invalid: t('fields.feeder.problem.key_invalid'),
    key_duplicate: t('fields.feeder.problem.key_duplicate'),
  };
  const colCount = 6 + SOURCE_IDS.length;

  /** What the row is used as: standard fields (engine-computed), custom fields, or nothing yet. */
  const bindingCell = (f: FeederFieldConfig) => {
    const standard = standardFieldsFrom(f.key);
    const custom = customByKey.get(f.key) ?? [];
    return (
      <td data-testid={`feeder-binding-${f.no}`}>
        <span className="stack" style={{ gap: 4 }}>
          {standard.map((s) => (
            <span key={s} className="row-wrap">
              <span className="mono small nowrap">{s}</span>
              {kindChip(FIELD_KINDS[s])}
            </span>
          ))}
          {custom.map((c) => (
            <span key={c.name} className="row-wrap" data-testid={`feeder-custom-${c.name}`}>
              <span className="chip ok mono">{c.name}</span>
              {kindChip(c.kind)}
              {mayEdit && <BindingActions field={c} idPrefix="feeder-custom" onEdit={() => setDialog({ mode: 'edit', field: c })} />}
            </span>
          ))}
          {standard.length === 0 && custom.length === 0 && (
            <span className="row-wrap">
              <span className="chip warn" data-testid={`feeder-unbound-${f.no}`}>
                {t('fields.binding.unbound.chip')}
              </span>
              {mayEdit && FEEDER_KEY.test(f.key) && (
                <button
                  type="button"
                  className="btn small"
                  data-testid={`feeder-bind-${f.no}`}
                  aria-label={t('fields.binding.bind.a11y', { key: f.key })}
                  onClick={() => setDialog({ mode: 'new', from: f.key })}
                >
                  {t('fields.binding.bind.action')}
                </button>
              )}
            </span>
          )}
        </span>
      </td>
    );
  };

  /** What the field(s) this row feeds mean — one line each; a dash while the row is not bound. */
  const descCell = (f: FeederFieldConfig) => {
    const standard = standardFieldsFrom(f.key);
    const custom = customByKey.get(f.key) ?? [];
    return (
      <td className="small feeder-desc" data-testid={`feeder-desc-${f.no}`}>
        {standard.length === 0 && custom.length === 0 ? (
          <span className="muted">—</span>
        ) : (
          <ul className="field-meanings">
            {standard.map((s) => (
              <li key={s}>
                <StandardMeaning field={s} />
              </li>
            ))}
            {custom.map((c) => (
              <li key={c.name}>
                <CustomMeaning field={c} rowName={f.name} />
              </li>
            ))}
          </ul>
        )}
      </td>
    );
  };

  return (
    <section className="card">
      <header className="card-head">
        <h2>{t('fields.feeder.title')}</h2>
        <span className="chip mute">{t('fields.feeder.count', { n: shown.length })}</span>
        <span className="spacer" />
        {!editing && mayEdit && (
          <button type="button" className="btn small" data-testid="feeder-edit" onClick={startEdit}>
            {t('fields.edit.action')}
          </button>
        )}
        {editing && (
          <>
            <button type="button" className="btn small" data-testid="feeder-add" onClick={addRow}>
              {t('fields.feeder.add.action')}
            </button>
            <button type="button" className="btn small" data-testid="feeder-cancel" onClick={cancel}>
              {t('btn.cancel.label')}
            </button>
            <button
              type="button"
              className="btn small primary"
              data-testid="feeder-save"
              disabled={problems.size > 0}
              onClick={save}
            >
              {t('fields.feeder.save.action')}
            </button>
          </>
        )}
      </header>
      <div className="tbl-wrap">
        <table className={editing ? undefined : 'feeder-table'}>
          <thead>
            <tr>
              <th style={editing ? { width: 44 } : undefined}>{t('fields.feeder.col.no')}</th>
              <th style={editing ? { width: 100 } : undefined}>{t('fields.feeder.col.de')}</th>
              <th>{t('fields.feeder.col.name')}</th>
              <th style={editing ? { width: 230 } : undefined}>{t('fields.feeder.col.key')}</th>
              <th style={editing ? { width: 90 } : undefined}>{t('fields.feeder.col.status')}</th>
              {SOURCE_IDS.map((id) => (
                <th key={id} style={editing ? { width: 96 } : undefined}>
                  {t(`source.${id}.name`)}
                </th>
              ))}
              {!editing && (
                <th>
                  {t('fields.feeder.col.binding')} <Tip align="right">{t('fields.binding.engine.tip')}</Tip>
                </th>
              )}
              {!editing && <th>{t('fields.feeder.col.desc')}</th>}
              {editing && <th style={{ width: 44 }} aria-label={t('fields.feeder.col.delete.a11y')} />}
            </tr>
          </thead>
          <tbody>
            {!editing &&
              shown.map((f) => (
                <tr key={f.no} data-testid={`feeder-row-${f.no}`}>
                  <td className="small">{f.no}</td>
                  <td className="mono small nowrap">{f.de ?? <span className="muted">—</span>}</td>
                  <td className="small">
                    {f.name}
                    {f.note && <Tip>{f.note}</Tip>}
                  </td>
                  <td className="mono small nowrap">
                    {f.key}
                  </td>
                  <td>
                    <span className={`chip ${f.status === 'New' ? '' : 'mute'}`}>{f.status}</span>
                  </td>
                  {SOURCE_IDS.map((id) => (
                    <td key={id} data-testid={`feeder-${f.no}-${id}`}>
                      {f.sends[id] ? <span className="good">✓</span> : <span className="muted">—</span>}
                    </td>
                  ))}
                  {bindingCell(f)}
                  {descCell(f)}
                </tr>
              ))}

            {editing &&
              shown.map((f) => {
                const rowIssues = problems.get(f.no) ?? [];
                const blocked = blockedBy(f.key);
                const label = f.key || String(f.no);
                // Wave 6: a key a custom field reads keeps its name, and its row, until it is unbound.
                const original = config.find((r) => r.no === f.no)?.key;
                const boundTo = original !== undefined ? (customByKey.get(original) ?? []).map((c) => c.name) : [];
                return [
                  <tr key={f.no} data-testid={`feeder-row-${f.no}`}>
                    <td className="small">{f.no}</td>
                    <td>
                      <input
                        type="text"
                        className="mono-input"
                        aria-label={t('fields.feeder.edit.de.a11y', { no: f.no })}
                        placeholder="—"
                        value={f.de ?? ''}
                        onChange={(e) => patchRow(f.no, { de: e.target.value === '' ? null : e.target.value })}
                      />
                    </td>
                    <td>
                      <span className="row-wrap" style={{ flexWrap: 'nowrap' }}>
                        <input
                          type="text"
                          className={rowIssues.includes('name') ? 'invalid' : ''}
                          aria-label={t('fields.feeder.edit.name.a11y', { no: f.no })}
                          value={f.name}
                          onChange={(e) => patchRow(f.no, { name: e.target.value })}
                        />
                        {f.note && <Tip>{f.note}</Tip>}
                      </span>
                    </td>
                    <td>
                      <span className="row-wrap" style={{ flexWrap: 'nowrap' }}>
                        <input
                          type="text"
                          className={`mono-input ${rowIssues.some((p) => p !== 'name') ? 'invalid' : ''}`}
                          aria-label={t('fields.feeder.edit.key.a11y', { no: f.no })}
                          spellCheck={false}
                          readOnly={boundTo.length > 0}
                          value={f.key}
                          onChange={(e) => setKey(f, e.target.value)}
                        />
                        {boundTo.length > 0 && <Tip>{t('fields.feeder.key.bound.tip', { fields: boundTo.join(', ') })}</Tip>}
                      </span>
                    </td>
                    <td>
                      <select
                        aria-label={t('fields.feeder.edit.status.a11y', { no: f.no })}
                        value={f.status}
                        onChange={(e) => patchRow(f.no, { status: e.target.value as FeederFieldConfig['status'] })}
                      >
                        {FEEDER_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </td>
                    {SOURCE_IDS.map((id) => (
                      <td key={id} data-testid={`feeder-${f.no}-${id}`}>
                        <input
                          type="checkbox"
                          aria-label={t('fields.feeder.edit.sends.a11y', { key: label, source: id })}
                          checked={f.sends[id]}
                          onChange={(e) => setSends(f.no, id, e.target.checked)}
                        />
                      </td>
                    ))}
                    <td className="nowrap">
                      <button
                        type="button"
                        className="btn small ghost rowdel"
                        aria-label={t('fields.feeder.delete.a11y', { key: label })}
                        disabled={blocked.rules.length > 0 || boundTo.length > 0}
                        onClick={() => removeRow(f.no)}
                      >
                        ✕
                      </button>
                      {blocked.rules.length > 0 && (
                        <Tip align="right">
                          {t('fields.feeder.delete.blocked.tip', { rules: blocked.rules.join(', '), fields: blocked.fields.join(', ') })}
                        </Tip>
                      )}
                      {blocked.rules.length === 0 && boundTo.length > 0 && (
                        <Tip align="right">{t('fields.feeder.key.bound.tip', { fields: boundTo.join(', ') })}</Tip>
                      )}
                    </td>
                  </tr>,
                  rowIssues.length > 0 && (
                    <tr key={`${f.no}-problems`}>
                      <td colSpan={colCount}>
                        <div className="why-missing" role="alert" data-testid={`feeder-problem-${f.no}`} style={{ margin: 0 }}>
                          {t('clause.row.label', { n: f.no })} — {rowIssues.map((p) => problemText[p]).join('; ')}
                        </div>
                      </td>
                    </tr>
                  ),
                ];
              })}
          </tbody>
        </table>
      </div>
      {!editing && <OffTableFields mayEdit={mayEdit} onEdit={(field) => setDialog({ mode: 'edit', field })} />}
      <BindingDialog open={dialog} onClose={() => setDialog(null)} />
    </section>
  );
}

/**
 * The fields no row of the table shows — drawn only when there are any, so nothing is orphaned:
 * a standard field no raw key feeds (the engine takes it from elsewhere — `channel_id` from the
 * envelope — or the payload lost its rows), and a custom field whose key is no longer in the
 * payload, with its Edit / Unbind.
 */
function OffTableFields({ mayEdit, onEdit }: { mayEdit: boolean; onEdit: (field: CustomField) => void }) {
  const config = useFeederConfig();
  const bindings = useCustomBindings();
  const standard = standardFieldsWithoutRow(config);
  const keys = new Set(config.map((r) => r.key));
  const orphans = bindings.list.filter((c) => !keys.has(c.from));
  if (standard.length === 0 && orphans.length === 0) return null;
  return (
    <div className="card-body offtable" data-testid="fields-offtable">
      <h3>{t('fields.offtable.title')}</h3>
      <ul className="offtable-list small">
        {standard.map((f) => (
          <li key={f} data-testid={`offtable-${f}`}>
            {kindChip(FIELD_KINDS[f])} <StandardMeaning field={f} />
            <span className="muted small">
              {' '}
              ({FIELD_DEPENDS[f].length === 0
                ? t('fields.offtable.computed', { from: FIELD_ORIGINS[f].feeder })
                : t('fields.offtable.norow', { keys: FIELD_DEPENDS[f].join(', ') })}
              )
            </span>
          </li>
        ))}
        {orphans.map((c) => (
          <li key={c.name} data-testid={`offtable-${c.name}`}>
            {kindChip(c.kind)} <CustomMeaning field={c} />
            <span className="muted small"> ({t('fields.offtable.orphan', { key: c.from })})</span>
            {mayEdit && (
              <>
                {' '}
                <BindingActions field={c} idPrefix="custom" onEdit={() => onEdit(c)} />
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function FieldManagementScreen() {
  return (
    <>
      <PageHead title={t('screen.fields.console.title')} lead={t('screen.fields.console.lead')} />
      <FeederView />
    </>
  );
}

